import { ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChangePasswordForm } from '@/features/auth';
import { useSession } from '@/stores/session';

/** Account = the signed-in Django user's identity (read-only) plus the
 *  change-password flow. Employment/HR data lives on the Employee Record page;
 *  MFA lives on the Security page. */
export function CabinetProfilePage() {
  const user = useSession((s) => s.user);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();

  return (
    <div className="max-w-3xl space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Account</h1>

      <Card>
        <CardHeader>
          <CardTitle>Your account</CardTitle>
          <CardDescription>
            Your sign-in identity. Names and email are managed by your administrator.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-2">
          <Field label="Name">{fullName || '—'}</Field>
          <Field label="Username">{user?.username || '—'}</Field>
          <Field label="Email">{user?.email || '—'}</Field>
          <Field label="Role">{user?.role || '—'}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Change password</CardTitle>
          <CardDescription>
            Choose a strong password you don’t use anywhere else.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChangePasswordForm />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5" /> Two-factor authentication
          </CardTitle>
          <CardDescription>Add an extra layer of security to your account.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/account/security">Manage security</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
